import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { formatApiError } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight, MailCheck, Clock } from "lucide-react";

export default function RequestAccess() {
  const location = useLocation();
  const nav = useNavigate();
  const prefill = location.state || {};
  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [agency, setAgency] = useState("");
  const [sellingAgent, setSellingAgent] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-access", {
        name,
        email,
        agency,
        selling_agent: sellingAgent,
        notes: notes || null,
      });
      setSubmitted({
        email,
        message:
          data?.message ||
          "Your request was submitted. An AWG admin will review and email you.",
      });
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Request failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative bg-[#0B132B] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1557846979-0b392058094e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYXIlMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTgyODExfDA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B] via-[#0B132B]/90 to-[#1C2541]/80" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <BrandLogo size="md" variant="light" />
          <div>
            <div className="inline-block px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.25em] font-bold mb-6">
              Request access
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.05]">
              Join the Advantage Warranty Group network.
            </h1>
            <p className="text-white/70 text-base mt-6 leading-relaxed">
              Tell us who you are and where you sell. An AWG admin will review
              and approve your account — you'll sign in with Google, no password
              required.
            </p>
          </div>
          <div className="text-xs text-white/40 tracking-[0.18em] uppercase">© Advantage Warranty Group</div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        {submitted ? (
          <div
            className="w-full max-w-sm space-y-6 text-center"
            data-testid="request-access-success"
          >
            <div className="lg:hidden flex justify-center"><BrandLogo size="md" /></div>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 grid place-items-center">
              <MailCheck size={30} className="text-[#D4AF37]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Request received
              </div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">
                We'll be in touch
              </h2>
              <p className="text-sm text-[#6C757D] mt-4 leading-relaxed">
                {submitted.message}
              </p>
              <p className="text-xs text-[#6C757D] mt-3">
                Once approved, sign in with Google using{" "}
                <span className="font-semibold text-[#0B132B]">{submitted.email}</span>.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#0B132B] text-xs">
              <Clock size={13} className="text-[#D4AF37]" />
              Awaiting admin review
            </div>
            <div>
              <Button
                onClick={() => nav("/login")}
                className="bg-[#0B132B] hover:bg-[#1C2541]"
                data-testid="request-access-back-to-login"
              >
                Back to sign in <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="w-full max-w-sm space-y-6"
            data-testid="request-access-form"
          >
            <div className="lg:hidden"><BrandLogo size="md" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Request access
              </div>
              <h2 className="font-display text-3xl font-black text-[#0B132B] mt-2">
                New agent?
              </h2>
              <p className="text-xs text-[#6C757D] mt-3 leading-relaxed">
                Fill this out and an AWG admin will review your request. Once
                approved, sign in with your Google account — no password needed.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  Full name
                </Label>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  data-testid="request-access-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  Email
                </Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  placeholder="you@agency.com"
                  data-testid="request-access-email-input"
                />
                <p className="text-xs text-[#6C757D] mt-1.5">
                  Must match the Google account you'll sign in with.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  Agency name
                </Label>
                <Input
                  required
                  value={agency}
                  onChange={(e) => setAgency(e.target.value)}
                  placeholder="e.g. Premier Auto Group"
                  className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  data-testid="request-access-agency-input"
                />
              </div>
              <label
                className="flex items-center gap-3 p-3 rounded-sm border border-[#0B132B]/15 hover:bg-[#F8F9FA] cursor-pointer transition-colors"
                data-testid="request-access-selling-agent-row"
              >
                <Checkbox
                  checked={sellingAgent}
                  onCheckedChange={(v) => setSellingAgent(Boolean(v))}
                  data-testid="request-access-selling-agent-checkbox"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0B132B]">Selling agent</div>
                  <div className="text-xs text-[#6C757D]">
                    I sell warranty products directly to dealerships.
                  </div>
                </div>
              </label>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  Anything else? (optional)
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Referring admin, existing agency contacts, etc."
                  className="mt-2 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  data-testid="request-access-notes-input"
                />
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600" data-testid="request-access-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold transition-colors"
              data-testid="request-access-submit-button"
            >
              {loading ? "Submitting…" : "Submit request"}
              <ArrowRight size={16} className="ml-1.5" />
            </Button>

            <p className="text-sm text-[#6C757D]">
              Already approved?{" "}
              <Link
                to="/login"
                className="text-[#0B132B] font-semibold hover:text-[#D4AF37]"
                data-testid="request-access-signin-link"
              >
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
